import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { useWSEvent } from "@/messaging/WSContext";
import { playMentionSound } from "@/messaging/sound";
import type {
  AnnouncementReactionGroup,
  Notification,
  NotificationList,
} from "@/messaging/types";
import { AnnouncementComposer } from "./AnnouncementComposer";
import { AnnouncementToasts } from "./AnnouncementToast";
import { NotificationDetail } from "./NotificationDetail";
import { announcementId } from "./shared";

type ListState = "idle" | "loading" | "ready" | "error";

interface NotificationsContextValue {
  unread: number;
  items: Notification[] | null;
  listState: ListState;
  /** Which bell instance owns the open dropdown, or null when closed. */
  panelOwner: string | null;
  /** Convenience for consumers that only care that *a* panel is open. */
  open: boolean;
  selected: Notification | null;
  composeOpen: boolean;
  canBroadcast: boolean;
  /** Open this bell's panel, or close it if it's already the owner. */
  togglePanel: (id: string) => void;
  closePanel: () => void;
  /** Callback ref: each bell registers its wrapper so the provider's
   *  single outside-click listener knows every legitimate anchor. */
  registerAnchor: (el: HTMLElement | null) => void;
  setSelected: (n: Notification | null) => void;
  setComposeOpen: (v: boolean) => void;
  refresh: () => Promise<void>;
  markRead: (id: string) => void;
  markAllRead: () => void;
  react: (announcementId: string, emoji: string) => void;
  patchReactions: (
    announcementId: string,
    reactions: Notification["reactions"],
  ) => void;
}

const Ctx = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const canBroadcast = !!user?.allowed_pages?.includes("/announcements");
  const myId = user?.id ?? null;

  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<Notification[] | null>(null);
  const [listState, setListState] = useState<ListState>("idle");
  const [panelOwner, setPanelOwner] = useState<string | null>(null);
  const [selected, setSelected] = useState<Notification | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [toasts, setToasts] = useState<Notification[]>([]);

  const open = panelOwner !== null;

  // ── panel ownership ─────────────────────────────────────────────────────
  //
  // The topbar mounts <NotificationsBell/> twice — once in the mobile
  // header, once in the desktop bar — and CSS hides one rather than
  // unmounting it. So "which bell is open" has to be a single owner id,
  // not a shared boolean: with a boolean both instances render a panel and
  // the hidden one's outside-click listener closes the visible one's panel
  // on mousedown, killing every click inside it before it lands.
  const togglePanel = useCallback((id: string) => {
    setPanelOwner((cur) => (cur === id ? null : id));
  }, []);
  const closePanel = useCallback(() => setPanelOwner(null), []);

  // Every mounted bell's wrapper element. A mousedown inside ANY of them
  // is "inside" — that's what makes the two instances cooperate.
  const anchorsRef = useRef<Set<HTMLElement>>(new Set());
  const registerAnchor = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    anchorsRef.current.add(el);
  }, []);

  // Panels are portaled to <body>, so containment can't be tested against
  // the anchor alone — they opt in with this data attribute instead.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      for (const el of anchorsRef.current) {
        if (el.isConnected && el.contains(t)) return;
      }
      if (t instanceof Element && t.closest("[data-notif-panel]")) return;
      setPanelOwner(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPanelOwner(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Detached anchors accumulate if a bell ever unmounts (route-level
  // remount, hot reload). Cheap sweep whenever the panel opens.
  useEffect(() => {
    if (!open) return;
    for (const el of anchorsRef.current) {
      if (!el.isConnected) anchorsRef.current.delete(el);
    }
  }, [open]);

  // ── reaction groups ─────────────────────────────────────────────────────
  //
  // The server fans one reaction payload out to every recipient, computing
  // `me` for the actor. Each client must re-derive it from `users` or
  // everyone else's pill lights up as theirs.
  const withMe = useCallback(
    (groups: AnnouncementReactionGroup[]): AnnouncementReactionGroup[] =>
      groups.map((g) => ({ ...g, me: g.users.some((u) => u.id === myId) })),
    [myId],
  );

  // Ids whose read-transition has already been counted against the badge.
  // Marking read arrives twice for the acting tab — once optimistically and
  // once as the server's `notification.read` echo — and rows fetched already
  // read must never decrement at all.
  const readSeenRef = useRef<Set<string>>(new Set());

  // ── badge only on mount (cheap aggregate) ───────────────────────────────
  const fetchUnread = useCallback(async () => {
    try {
      const r = await api<{ unread: number }>("/api/notifications/unread-count");
      setUnread(r.unread);
    } catch {
      /* keep prior */
    }
  }, []);

  useEffect(() => {
    if (user) void fetchUnread();
  }, [user, fetchUnread]);

  // ── full list loads lazily when the bell opens ──────────────────────────
  const refresh = useCallback(async () => {
    setListState((s) => (s === "ready" ? s : "loading"));
    try {
      const r = await api<NotificationList>("/api/notifications?limit=30");
      // Anything the server already considers read is recorded here, so a
      // later markRead() on that row is a no-op instead of decrementing a
      // badge it was never counted in.
      for (const n of r.items) if (n.read_at) readSeenRef.current.add(n.id);
      setItems(r.items.map((n) => ({ ...n, reactions: withMe(n.reactions) })));
      setUnread(r.unread_count);
      setListState("ready");
    } catch {
      setListState("error");
    }
  }, [withMe]);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  // ── read state ──────────────────────────────────────────────────────────
  //
  // Guarded on the client rather than by dropping the server's echo, because
  // the user's OTHER tabs genuinely need that frame to update their badge.
  const applyRead = useCallback((id: string, at: string) => {
    if (readSeenRef.current.has(id)) return;
    readSeenRef.current.add(id);
    setItems((prev) =>
      prev ? prev.map((n) => (n.id === id ? { ...n, read_at: n.read_at ?? at } : n)) : prev,
    );
    setUnread((u) => Math.max(0, u - 1));
  }, []);

  const markRead = useCallback(
    (id: string) => {
      if (readSeenRef.current.has(id)) return;
      applyRead(id, new Date().toISOString());
      void api(`/api/notifications/${id}/read`, { method: "POST" }).catch(() => undefined);
    },
    [applyRead],
  );

  const markAllRead = useCallback(() => {
    const now = new Date().toISOString();
    setItems((prev) => {
      if (prev) for (const n of prev) readSeenRef.current.add(n.id);
      return prev ? prev.map((n) => ({ ...n, read_at: n.read_at ?? now })) : prev;
    });
    setUnread(0);
    void api("/api/notifications/read-all", { method: "POST" }).catch(() => undefined);
  }, []);

  // ── reactions ───────────────────────────────────────────────────────────
  const patchReactions = useCallback(
    (aid: string, reactions: Notification["reactions"]) => {
      const mine = withMe(reactions);
      const patch = (n: Notification) =>
        n.payload?.announcement_id === aid ? { ...n, reactions: mine } : n;
      setItems((prev) => (prev ? prev.map(patch) : prev));
      setToasts((prev) => prev.map(patch));
      setSelected((cur) =>
        cur && cur.payload?.announcement_id === aid ? { ...cur, reactions: mine } : cur,
      );
    },
    [withMe],
  );

  const react = useCallback(
    async (aid: string, emoji: string) => {
      try {
        const groups = await api<AnnouncementReactionGroup[]>(
          `/api/announcements/${aid}/reactions`,
          { method: "PUT", json: { emoji } },
        );
        patchReactions(aid, groups);
      } catch {
        /* WS reconciles */
      }
    },
    [patchReactions],
  );

  // ── toasts ──────────────────────────────────────────────────────────────
  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const openToast = useCallback(
    (n: Notification) => {
      markRead(n.id);
      dismissToast(n.id);
      if (announcementId(n)) setSelected(n);
    },
    [dismissToast, markRead],
  );

  // ── live WS ─────────────────────────────────────────────────────────────
  useWSEvent("notification.new", (ev) => {
    const incoming: Notification = {
      ...ev.notification,
      reactions: withMe(ev.notification.reactions ?? []),
    };
    let isNew = true;
    setItems((prev) => {
      if (!prev) return prev; // list not loaded yet — badge below still bumps
      if (prev.some((n) => n.id === incoming.id)) {
        isNew = false;
        return prev;
      }
      return [incoming, ...prev].slice(0, 50);
    });
    if (!isNew) return; // duplicate frame — don't double-count or re-toast
    setUnread((u) => u + 1);

    // Broadcast-shaped notifications (owner announcements, the monthly
    // leaderboard rollup) surface immediately as a toast — the bell badge
    // alone is too easy to miss while you're working.
    if (announcementId(incoming)) {
      setToasts((prev) => [incoming, ...prev.filter((t) => t.id !== incoming.id)].slice(0, 3));
      try {
        playMentionSound();
      } catch {
        /* ignore */
      }
    } else if (typeof document !== "undefined" && document.hidden) {
      try {
        playMentionSound();
      } catch {
        /* ignore */
      }
    }
  });

  useWSEvent("notification.read", (ev) => {
    applyRead(ev.notification_id, ev.read_at);
  });

  useWSEvent("notification.read_all", (ev) => {
    setItems((prev) => {
      if (prev) for (const n of prev) readSeenRef.current.add(n.id);
      return prev ? prev.map((n) => ({ ...n, read_at: n.read_at ?? ev.read_at })) : prev;
    });
    setUnread(0);
  });

  useWSEvent("announcement.reaction_toggled", (ev) => {
    patchReactions(ev.announcement_id, ev.reactions as Notification["reactions"]);
  });

  const value = useMemo<NotificationsContextValue>(
    () => ({
      unread,
      items,
      listState,
      panelOwner,
      open,
      selected,
      composeOpen,
      canBroadcast,
      togglePanel,
      closePanel,
      registerAnchor,
      setSelected,
      setComposeOpen,
      refresh,
      markRead,
      markAllRead,
      react,
      patchReactions,
    }),
    [
      unread,
      items,
      listState,
      panelOwner,
      open,
      selected,
      composeOpen,
      canBroadcast,
      togglePanel,
      closePanel,
      registerAnchor,
      refresh,
      markRead,
      markAllRead,
      react,
      patchReactions,
    ],
  );

  const selectedAnnouncementId = selected ? announcementId(selected) : null;

  return (
    <Ctx.Provider value={value}>
      {children}
      {/* Rendered ONCE here (not per-bell) so the two topbar bell instances
          can't spawn duplicate/competing modals. */}
      <AnnouncementToasts
        toasts={toasts}
        onOpen={openToast}
        onDismiss={dismissToast}
        onReact={react}
      />
      {composeOpen && <AnnouncementComposer onClose={() => setComposeOpen(false)} />}
      {selected && selectedAnnouncementId && (
        <NotificationDetail
          n={selected}
          onClose={() => setSelected(null)}
          onReact={(emoji) => react(selectedAnnouncementId, emoji)}
        />
      )}
    </Ctx.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useNotifications must be used inside <NotificationsProvider>");
  return v;
}
